import { useCallback, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import LoadingFullPage from 'components/LoadingFullPage'
import Select, { SelectProps } from 'components/Select'
import { QUERY_KEYS } from 'constants/keys'
import { ROUTES } from 'constants/routes'
import { PERMISSIONS_METHODS_OPTIONS } from 'constants/types'
import { useGlobalState } from 'contexts/global-state'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ApiService } from 'services/api'
import { removeDuplicatedArrayOfObject } from 'utils/removeDuplicatedArrayOfObject'

import Delete from './delete'
import { schema } from './form-validation'

type OnSave = (permission: PermissionsApiRequest) => Promise<void>

type AddProps = {
  permission?: Permission
  onSave?: OnSave
  isEditMode?: boolean
}

export default function Add({
  permission,
  onSave,
  isEditMode = false,
}: AddProps) {
  const navigate = useNavigate()

  const { openSuccessToast, openErrorToast } = useGlobalState()

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<PermissionsApiRequest>({
    resolver: yupResolver(schema),
  })

  const [formUpdated, setFormUpdated] = useState<boolean>(false)

  const [deleteConfirmation, setDeleteConfirmation] = useState<
    Permission | undefined
  >()

  const { data: permissionsData } = useQuery<SelectProps['options']>(
    QUERY_KEYS.PERMISSIONS.NAMES_AND_IDS,
    async () => {
      const responsePermissions = await ApiService.Permissions.getAll()
      const filtered = removeDuplicatedArrayOfObject(responsePermissions, {
        field: 'grouped',
      })
      return filtered.map(
        (item) =>
          item && {
            label: item.grouped as string,
            value: item.grouped as string,
          },
      ) as SelectProps['options']
    },
  )

  useEffect(() => {
    if (isEditMode && !!permission && !formUpdated) {
      reset({
        name: permission.name,
        description: permission.description,
        method: permission.method,
        base_url: permission.base_url,
        path: permission.path,
        grouped: permission.grouped,
        function: permission.function,
      })

      setFormUpdated(true)
    }
  }, [isEditMode, permission, formUpdated, reset, setValue])

  const inputProps = useCallback(
    (fieldName: keyof PermissionsApiRequest) => {
      if (isEditMode) return { InputLabelProps: { shrink: true } }

      return { InputLabelProps: { shrink: !!watch(fieldName) } }
    },
    [isEditMode, watch],
  )

  const addNewPermission = useMutation({
    mutationFn: async (permission: PermissionsApiRequest) => {
      await ApiService.Permissions.create(permission)
    },
    onSuccess: (_data, permission: PermissionsApiRequest) => {
      openSuccessToast({
        message: `A permissão ${permission.name} foi adicionada com sucesso!`,
      })

      navigate(ROUTES.PERMISSIONS.LIST)
    },
    onError: (error: ApiError, permission: PermissionsApiRequest) => {
      openErrorToast({
        message: error.message || `Erro ao adicionar ${permission.name}!`,
      })
    },
  })

  if (isEditMode && !!permission && formUpdated === false) {
    return <LoadingFullPage />
  }

  return (
    <>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth={false}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link
              underline="hover"
              color="inherit"
              href={ROUTES.PERMISSIONS.LIST}
            >
              Permissões
            </Link>

            <Typography color="text.primary">
              {isEditMode ? 'Editar' : 'Adicionar'}
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ArrowBackIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  navigate(-1)
                }}
              />
              <Typography sx={{ m: 1 }} variant="h4">
                {isEditMode ? 'Editar' : 'Adicionar'} permissões
              </Typography>
            </Box>

            <Box sx={{ m: 1, display: 'flex' }}>
              {isEditMode && (
                <Box sx={{ m: 1 }}>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={() => {
                      setDeleteConfirmation(permission as Permission)
                    }}
                  >
                    Remover
                  </Button>
                </Box>
              )}

              <Box sx={{ m: 1 }}>
                <Button
                  color="info"
                  variant="contained"
                  onClick={() => {
                    navigate(ROUTES.PERMISSIONS.LIST)
                  }}
                >
                  Listar
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <form
              onSubmit={handleSubmit((permission: PermissionsApiRequest) => {
                if (isEditMode) {
                  onSave?.(permission)
                  return
                }

                addNewPermission.mutate(permission)
              })}
            >
              <Card>
                <CardContent>
                  <Grid container spacing={3} xs={12}>
                    <Grid item xs={6}>
                      <TextField
                        error={!!errors.name?.message}
                        fullWidth
                        helperText={errors.name?.message}
                        label="Nome"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        {...inputProps('name')}
                        {...register('name')}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        error={!!errors.base_url?.message}
                        fullWidth
                        helperText={errors.base_url?.message}
                        label="URL de base"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        {...inputProps('base_url')}
                        {...register('base_url')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        error={!!errors.path?.message}
                        fullWidth
                        helperText={errors.path?.message}
                        label="Caminho"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        {...inputProps('path')}
                        {...register('path')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="method"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Select
                              label="Método"
                              id="method"
                              onChange={onChange}
                              value={value}
                              autoFill={isEditMode}
                              options={PERMISSIONS_METHODS_OPTIONS}
                              error={!!errors.method?.message}
                              helperText={errors.method?.message}
                            />
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="grouped"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Select
                              label="Grupo"
                              id="grouped"
                              onChange={onChange}
                              value={value}
                              autoFill={isEditMode}
                              options={permissionsData || []}
                              error={!!errors.grouped?.message}
                              helperText={errors.grouped?.message}
                            />
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        error={!!errors.function?.message}
                        fullWidth
                        helperText={errors.function?.message}
                        label="Função"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        {...inputProps('function')}
                        {...register('function')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={!!errors.description?.message}
                        fullWidth
                        helperText={errors.description?.message}
                        label="Descrição"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        multiline
                        minRows={4}
                        {...inputProps('description')}
                        {...register('description')}
                      />
                    </Grid>
                  </Grid>

                  <Box>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      style={{
                        marginLeft: 'auto',
                        display: 'block',
                        marginTop: '1em',
                      }}
                    >
                      {isEditMode ? 'Editar' : 'Adicionar'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </form>
          </Box>
        </Container>
      </Box>

      <Delete
        opened={!!deleteConfirmation}
        closeModal={() => setDeleteConfirmation(undefined)}
        permission={deleteConfirmation as Permission}
      />
    </>
  )
}
